import type { Layer, Project } from '../core/schema';
import { sourceTimeAt } from '../core/timeline';
import { getAssetBlob } from './persistence';

interface ActiveSource { source: AudioBufferSourceNode; gain: GainNode }

class AudioEngine {
  private context: AudioContext | null = null;
  private cache = new Map<string, AudioBuffer>();
  private sources: ActiveSource[] = [];
  private destination: MediaStreamAudioDestinationNode | null = null;

  private ensureContext() {
    if (!this.context) this.context = new AudioContext();
    return this.context;
  }

  async decode(projectId: string, assetId: string): Promise<AudioBuffer | null> {
    const key = `${projectId}:${assetId}`;
    if (this.cache.has(key)) return this.cache.get(key)!;
    const blob = await getAssetBlob(projectId, assetId);
    if (!blob) return null;
    try {
      const buffer = await this.ensureContext().decodeAudioData(await blob.arrayBuffer());
      this.cache.set(key, buffer);
      return buffer;
    } catch { return null; }
  }

  stop() {
    for (const active of this.sources) { try { active.source.stop(); } catch { /* already stopped */ } }
    this.sources = [];
  }

  async play(project: Project, time: number, routeToCapture = false): Promise<MediaStream | null> {
    this.stop();
    const context = this.ensureContext();
    await context.resume();
    if (routeToCapture && !this.destination) this.destination = context.createMediaStreamDestination();
    const composition = project.compositions[project.activeCompositionId];
    const layers = composition.layerIds.map((id) => project.layers[id]).filter((layer): layer is Layer => Boolean(layer && (layer.type === 'audio' || layer.type === 'video') && !layer.muted && time < layer.start + layer.duration));
    const anySolo = layers.some((layer) => layer.solo);
    for (const layer of layers) {
      if (!layer.assetId || (anySolo && !layer.solo)) continue;
      const buffer = await this.decode(project.id, layer.assetId);
      if (!buffer) continue;
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.playbackRate.value = layer.speed;
      const gain = context.createGain();
      gain.gain.value = layer.volume;
      source.connect(gain);
      gain.connect(context.destination);
      if (routeToCapture && this.destination) gain.connect(this.destination);
      const delay = Math.max(0, layer.start - time);
      const offset = time > layer.start ? sourceTimeAt(layer, time) : layer.inPoint;
      const playable = Math.max(0.01, layer.duration - Math.max(0, time - layer.start));
      try { source.start(context.currentTime + delay, Math.min(offset, Math.max(0, buffer.duration - 0.01)), Math.min(playable * layer.speed, buffer.duration)); }
      catch { continue; }
      this.sources.push({ source, gain });
    }
    return routeToCapture ? this.destination?.stream ?? null : null;
  }
}

export const audioEngine = new AudioEngine();
